// Compare-mode rendering helpers. Loads a sheet PDF, rasterizes it with
// pdf.js, then recolors the linework so two versions can be overlaid with
// distinct colors.
//
// Recolor strategy: keep the white background (so we can overlay with CSS
// `mix-blend-mode: multiply` without dimming the background), and remap dark
// pixels to the chosen color with intensity proportional to their original
// darkness. White × anything = anything, so the bottom canvas's lines and
// the top canvas's lines both stay visible; where they cross, the multiply
// blend produces a darker mix that signals "both versions have this".

import * as pdfjsLib from 'pdfjs-dist';
import { getSheetPdfUrl } from './api';

if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url
  ).toString();
}

// Shared swatch palette for both sides of the comparison. Defaults are
// A=red, B=blue per typical redline-vs-blueline drafting convention. Black
// and gray are useful when one side of the comparison should read as
// "the original" while the other gets a colored highlight.
export const COMPARE_SWATCHES = [
  { id: 'red',   label: 'Red',   rgb: [220,  38,  38] },
  { id: 'blue',  label: 'Blue',  rgb: [ 37,  99, 235] },
  { id: 'green', label: 'Green', rgb: [ 22, 163,  74] },
  { id: 'black', label: 'Black', rgb: [  0,   0,   0] },
  { id: 'gray',  label: 'Gray',  rgb: [107, 114, 128] },
];
export const DEFAULT_COLOR_A_ID = 'red';
export const DEFAULT_COLOR_B_ID = 'blue';

export function rgbForSwatch(id) {
  const s = COMPARE_SWATCHES.find((x) => x.id === id);
  return s ? s.rgb : [0, 0, 0];
}

// Render a sheet PDF's first page into an offscreen canvas at the given
// scale, white background, anti-aliased. Returns { canvas, width, height,
// originalImageData } where originalImageData is a snapshot of the raw
// rendered pixels — recolorCanvasInPlace() works against this snapshot so
// the user can change colors live without re-rasterizing the PDF.
export async function renderSheetForCompare(sheetId, scale = 1.5) {
  const url = getSheetPdfUrl(sheetId);
  const doc = await pdfjsLib.getDocument(url).promise;
  try {
    const page = await doc.getPage(1);
    const vp   = page.getViewport({ scale });
    const w    = Math.round(vp.width);
    const h    = Math.round(vp.height);

    const canvas = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    await page.render({ canvasContext: ctx, viewport: vp }).promise;

    // Snapshot the raw render so subsequent recolors can re-derive cleanly
    // without re-running pdf.js.
    const originalImageData = ctx.getImageData(0, 0, w, h);

    return { canvas, width: w, height: h, originalImageData };
  } finally {
    try { await doc.destroy(); } catch (_) {}
  }
}

// Repaint `canvas` so dark pixels become `[r,g,b]` with intensity proportional
// to their original darkness, and bright pixels stay white. Reads from
// `originalImageData` (the raw render snapshot) so calling this repeatedly
// with different colors gives you the same lines, just tinted differently.
//
// We tint into the RGB channels rather than punching the alpha because the
// overlay uses `mix-blend-mode: multiply` — both layers need an opaque white
// backdrop so the multiplication preserves background where neither side
// drew anything.
export function recolorCanvasInPlace(canvas, originalImageData, [r, g, b]) {
  const ctx = canvas.getContext('2d');
  // Work on a copy so the snapshot stays clean for the next recolor.
  const out = new ImageData(
    new Uint8ClampedArray(originalImageData.data),
    originalImageData.width,
    originalImageData.height
  );
  const d = out.data;
  for (let i = 0; i < d.length; i += 4) {
    // 0 = pure black (full color), 1 = pure white (background untouched).
    const lum = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / 255;
    // Linearly interpolate between target color (lum=0) and white (lum=1).
    d[i]     = Math.round(r + (255 - r) * lum);
    d[i + 1] = Math.round(g + (255 - g) * lum);
    d[i + 2] = Math.round(b + (255 - b) * lum);
    // Alpha stays 255 — opaque white wherever there was no linework.
  }
  ctx.putImageData(out, 0, 0);
}
